/* global Spreedly */

export default class SpreedlyPaymentService {
  environmentKey = null;

  onScriptError = (/* msg, source, lineno, colno, err */) => {
    throw 'Script error not handled';
  };

  constructor(envKey) {
    this.environmentKey = envKey;
  }

  async install() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.crossOrigin = 'anonymous';

      // Required for PCI compliance. See: https://developer.spreedly.com/docs/securing-iframe#script-integrity
      script.src = 'https://core.spreedly.com/iframe/iframe-1.179.min.js';
      script.integrity = 'sha384-2XxczN7UZjq8uta9GXH5/3gTExtgznBhMNIPswEGgt7Qeo9B0p88eFW3YpxcPXJK'; // https://developer.spreedly.com/docs/iframe-integrity-hashes

      script.onerror = (msg, source, lineno, colno, err) => {
        this.onScriptError(msg, source, lineno, colno, err);
        return reject();
      };

      script.onload = () => {
        return resolve();
      };

      document.head.appendChild(script);
    });
  }

  destroy() {
    if (typeof Spreedly !== 'undefined') {
      Spreedly.removeHandlers();
    }
  }

  /**
   * Install Spreedly, and trigger provided callback. Note that this callback is
   * not strictly needed. It's purpose is to provide a clearly documented hook
   * for installing dependent work.
   */
  installForm({ cvvElId, numberElId }) {
    Spreedly.init(this.environmentKey, {
      cvvEl: cvvElId,
      numberEl: numberElId,
      nonce: document.head.querySelector('[name~=spreedly_nonce][content]').content,
      certificateToken: document.head.querySelector('[name~=spreedly_certificate_token][content]')
        .content,
      timestamp: document.head.querySelector('[name~=spreedly_timestamp][content]').content,
      signature: document.head.querySelector('[name~=spreedly_signature][content]').content,
    });
  }

  on(eventName, callback) {
    Spreedly.on(eventName, callback.bind(this, Spreedly));
  }

  transferFocus(fieldType) {
    Spreedly.transferFocus(fieldType);
  }

  tokenizeCreditCard({
    fullName,
    email,
    month,
    year,
    address1,
    address2,
    city,
    state,
    zip,
    country,
  }) {
    fetch('/spreedly_iframe_security_params', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((response) => {
        Spreedly.nonce = response.nonce;
        Spreedly.certificateToken = response.certificate_token;
        Spreedly.timestamp = response.timestamp;
        Spreedly.signature = response.signature;

        Spreedly.tokenizeCreditCard({
          full_name: fullName,
          email,
          month,
          year,
          address1,
          address2,
          city,
          state,
          zip,
          country,
        });
      });
  }

  validate({ fullName, email, month, year }) {
    Spreedly.validate({
      full_name: fullName,
      email,
      month,
      year,
    });
  }

  /**
   * @param {hash} options
   *
   * @param {string} options.hiddenIframeLocation The DOM node that you'd like to inject hidden iframes (required)
   *
   * @param {string} options.challengeIframeLocation The DOM node that you'd like to inject the challenge flow (required)
   *
   * @param {string} options.transactionToken The token for the transaction - used to poll for state (required)
   *
   * @param {string=} options.challengeIframeClasses The css classes that you'd like to apply to the challenge iframe.
   *   Note: This is where you'll change the height and width of the challenge
   *         iframe. You'll need to match the height and width option that you
   *         selected when collecting browser data with `Spreedly.ThreeDS.serialize`.
   *         For instance if you selected '04' for browserSize you'll need to have a
   *         CSS class that has width and height of 600px by 400px.
   *
   * @returns Spreedly.ThreeDS.LifeCycle
   * @see https://docs.spreedly.com/reference/iframe/v1/#new
   */
  build3DSLifecycle({
    hiddenIframeLocation,
    challengeIframeLocation,
    transactionToken,
    challengeIframeClasses,
  }) {
    return new Spreedly.ThreeDS.Lifecycle({
      // The environmentKey field is required, but if omitted, you will receive
      // a console warning message and the transaction will still succeed.
      environmentKey: this.environmentKey,
      hiddenIframeLocation,
      challengeIframeLocation,
      transactionToken,

      challengeIframeClasses,
    });
  }
}
